// AF-3.16 — NextBestActionCard.
//
// Renders the backend's own primary_action / next_best_action /
// next_control_action / primary_control_decision text VERBATIM. This
// component contains NO copy-rewriting logic -- it never turns "ready
// for authorized distribution execution" into "publish now" or similar
// (spec Section 7). The only thing this component decides is layout.

import { ArrowRight } from "@phosphor-icons/react";
import { ReadinessBadge } from "./Badges";
import { Button } from "@/components/ui/button";

export function NextBestActionCard({ action, reason, readiness, blockers = [], onViewEvidence, testId = "next-best-action-card" }) {
  if (!action) {
    return (
      <div data-testid={testId} className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-5 shadow-[var(--shadow-card)]">
        <p className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-tertiary)] mb-2">Next Best Action</p>
        <p className="text-[12px] text-[var(--text-tertiary)]">No action can be determined yet -- insufficient evidence.</p>
      </div>
    );
  }
  return (
    <div data-testid={testId} className="rounded-[var(--radius-card)] border border-[var(--accent-border)] bg-[var(--accent-light)] p-5 shadow-[var(--shadow-card)]">
      <p className="text-[11px] font-mono uppercase tracking-wide text-[var(--accent)] mb-2">Next Best Action</p>
      <p className="text-[16px] font-semibold text-[var(--text-primary)] mb-2" data-testid="next-best-action-text">{action}</p>
      {reason && (
        <div className="mb-3">
          <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)] mb-1">Why</p>
          <p className="text-[12px] text-[var(--text-secondary)]">{reason}</p>
        </div>
      )}
      <div className="flex items-center gap-3 flex-wrap">
        {readiness && (
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Readiness</span>
            <ReadinessBadge value={readiness} />
          </div>
        )}
        {blockers.length > 0 && (
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Blockers</span>
            <span className="text-[10px] text-[var(--error)]">{blockers.join(", ")}</span>
          </div>
        )}
        {onViewEvidence && (
          <Button data-testid={`${testId}-evidence`} variant="link" size="sm" className="ml-auto text-[11px] text-[var(--accent)] gap-1" onClick={onViewEvidence}>
            View evidence <ArrowRight size={12} />
          </Button>
        )}
      </div>
    </div>
  );
}

export function AlternativeActionsList({ alternatives = [] }) {
  if (!alternatives.length) return null;
  return (
    <div data-testid="alternative-actions-list" className="mt-3 space-y-1.5">
      <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Alternatives (max 3)</p>
      {alternatives.slice(0, 3).map((alt, i) => (
        <div key={i} className="flex items-center justify-between text-[11px] text-[var(--text-secondary)] px-2 py-1.5 rounded bg-[var(--surface-sunken)]" data-testid="alternative-action-item">
          <span>{alt.action}</span>
          {alt.readiness && <ReadinessBadge value={alt.readiness} />}
        </div>
      ))}
    </div>
  );
}
